import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { getTodayEastern } from '../../lib/dateHelpers'
import { notifyWorkers, getOwnerIds } from '../../lib/notifyWorker'
import FindReplacementModal from './FindReplacementModal'

// Owner approval window — anything past this many days from "today" is greyed
// out on the calendar with a "more dates available closer to that time" hint.
const HORIZON_DAYS = 92 // ~3 months
const MONTHS_VISIBLE = 3

const MONTH_LABELS = ['JANUARY','FEBRUARY','MARCH','APRIL','MAY','JUNE','JULY','AUGUST','SEPTEMBER','OCTOBER','NOVEMBER','DECEMBER']
const DOW_LABELS = ['S','M','T','W','T','F','S']

// Convert "YYYY-MM-DD" → noon-Eastern Date for safe arithmetic / display.
function toLocalDate(iso) { return new Date(iso + 'T12:00:00') }
function toIso(d) {
  var y = d.getFullYear()
  var m = String(d.getMonth() + 1).padStart(2, '0')
  var dd = String(d.getDate()).padStart(2, '0')
  return y + '-' + m + '-' + dd
}
function addDays(iso, n) {
  var d = toLocalDate(iso)
  d.setDate(d.getDate() + n)
  return toIso(d)
}

// Build a 6-row x 7-col grid for `month` so the layout is always 42 cells.
function getMonthGrid(year, month) {
  var first = new Date(year, month, 1)
  var startPad = first.getDay() // 0..6 Sun..Sat
  var cells = []
  // Pre-pad with previous-month days to keep the grid aligned.
  for (var i = 0; i < startPad; i++) {
    var pre = new Date(year, month, -startPad + i + 1)
    cells.push({ date: pre, iso: toIso(pre), inMonth: false })
  }
  var lastDate = new Date(year, month + 1, 0).getDate()
  for (var d = 1; d <= lastDate; d++) {
    var cur = new Date(year, month, d)
    cells.push({ date: cur, iso: toIso(cur), inMonth: true })
  }
  while (cells.length < 42) {
    var trail = new Date(year, month + 1, cells.length - startPad - lastDate + 1)
    cells.push({ date: trail, iso: toIso(trail), inMonth: false })
  }
  return cells
}

function fmtNice(iso) {
  if (!iso) return ''
  return toLocalDate(iso).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
}

export default function BlockedDatesCalendar() {
  var { user, profile } = useAuth()
  var [blocks, setBlocks] = useState([])
  var [loading, setLoading] = useState(true)
  var [tapped, setTapped] = useState(null) // { iso, reason }
  var [rangeMode, setRangeMode] = useState(false)
  var [rangeStart, setRangeStart] = useState(null)
  var [rangeEnd, setRangeEnd] = useState(null)
  var [rangeReason, setRangeReason] = useState('')
  var [submitting, setSubmitting] = useState(false)
  var [findReplacement, setFindReplacement] = useState(null)

  var today = getTodayEastern()
  var horizonIso = addDays(today, HORIZON_DAYS)

  var load = useCallback(async function() {
    if (!user?.id) return
    setLoading(true)
    var { data, error } = await supabase.from('worker_blocked_dates')
      .select('*').eq('user_id', user.id).order('blocked_date', { ascending: true })
    if (error) { console.error('[BlockedDatesCalendar] load failed', error) }
    setBlocks(data || [])
    setLoading(false)
  }, [user?.id])

  useEffect(function() { load() }, [load])

  // Map iso → block row for fast lookup in cell rendering.
  var blockByIso = useMemo(function() {
    var m = {}
    blocks.forEach(function(b) { m[b.blocked_date] = b })
    return m
  }, [blocks])

  // The 3 month panes to render, starting at today's month.
  var monthPanes = useMemo(function() {
    var d = toLocalDate(today)
    var out = []
    for (var i = 0; i < MONTHS_VISIBLE; i++) {
      var y = d.getFullYear()
      var m = d.getMonth() + i
      var date = new Date(y, m, 1)
      out.push({ year: date.getFullYear(), month: date.getMonth() })
    }
    return out
  }, [today])

  // Insert a single block. Returns the inserted row or null.
  var insertOne = async function(iso, reason) {
    var { data, error } = await supabase.from('worker_blocked_dates')
      .insert({ user_id: user.id, blocked_date: iso, reason: reason || null, status: 'pending' })
      .select().single()
    if (error) {
      // Unique-violation (already blocked) is silent — user double-tapped.
      if (error.code !== '23505') console.error('[BlockedDatesCalendar] insert failed', error)
      return null
    }
    return data
  }

  // Notify owners after a successful insert (or batch). Best-effort.
  var notifyOwnersOfBlocks = async function(rows) {
    if (!rows || rows.length === 0) return
    try {
      var ownerIds = await getOwnerIds()
      if (!ownerIds || ownerIds.length === 0) return
      var summary
      if (rows.length === 1) summary = fmtNice(rows[0].blocked_date) + (rows[0].reason ? ' — ' + rows[0].reason : '')
      else summary = rows.length + ' days starting ' + fmtNice(rows[0].blocked_date) + (rows[0].reason ? ' — ' + rows[0].reason : '')
      await notifyWorkers(ownerIds, {
        type: 'blocked_date_requested',
        title: (profile?.full_name || 'A worker') + ' requested time off',
        message: summary,
        link: '/schedule',
        relatedId: rows[0].id,
      })
    } catch (e) {
      console.warn('[BlockedDatesCalendar] notify owners failed', e)
    }
  }

  var commitTap = async function() {
    if (!tapped || submitting) return
    setSubmitting(true)
    var row = await insertOne(tapped.iso, tapped.reason)
    setTapped(null)
    if (row) await notifyOwnersOfBlocks([row])
    await load()
    setSubmitting(false)
  }

  var commitRange = async function() {
    if (!rangeStart || !rangeEnd || submitting) return
    var start = rangeStart, end = rangeEnd
    if (end < start) { var t = start; start = end; end = t }
    setSubmitting(true)
    var inserted = []
    var cursor = start
    while (cursor <= end) {
      // Skip Sundays — they're already off.
      var dow = toLocalDate(cursor).getDay()
      if (dow !== 0) {
        var row = await insertOne(cursor, rangeReason)
        if (row) inserted.push(row)
      }
      cursor = addDays(cursor, 1)
    }
    setRangeMode(false)
    setRangeStart(null); setRangeEnd(null); setRangeReason('')
    if (inserted.length > 0) await notifyOwnersOfBlocks(inserted)
    await load()
    setSubmitting(false)
  }

  var deletePending = async function(block) {
    if (block.status !== 'pending') { alert('Approved blocks must be removed by the owner.'); return }
    var { error } = await supabase.from('worker_blocked_dates').delete().eq('id', block.id)
    if (error) { alert('Could not cancel: ' + error.message); return }
    await load()
  }

  var clickCell = function(cell, isPast, isSunday, beyond) {
    if (isPast || isSunday || beyond) return
    if (rangeMode) {
      if (!rangeStart) { setRangeStart(cell.iso); setRangeEnd(cell.iso); return }
      setRangeEnd(cell.iso)
      return
    }
    var existing = blockByIso[cell.iso]
    if (existing) {
      if (existing.status === 'pending') {
        if (window.confirm('Cancel this pending block for ' + fmtNice(cell.iso) + '?')) deletePending(existing)
      }
      return
    }
    setTapped({ iso: cell.iso, reason: '' })
  }

  var inRange = function(iso) {
    if (!rangeStart || !rangeEnd) return false
    var lo = rangeStart, hi = rangeEnd
    if (hi < lo) { var t = lo; lo = hi; hi = t }
    return iso >= lo && iso <= hi
  }

  var nonSundayBlocks = blocks // we render approved/pending/denied — all of them
  var pending = nonSundayBlocks.filter(function(b) { return b.status === 'pending' })
  var approved = nonSundayBlocks.filter(function(b) { return b.status === 'approved' })
  var denied = nonSundayBlocks.filter(function(b) { return b.status === 'denied' })

  return (
    <div>
      <div style={st.legendRow}>
        <Legend dot="#b48c3c" label="Pending" />
        <Legend dot="#e05c5c" label="Approved" />
        <Legend dot="rgba(255,255,255,0.25)" label="Available" filled={false} />
      </div>

      <div style={st.toolbarRow}>
        <button
          type="button"
          onClick={function() { setRangeMode(!rangeMode); setRangeStart(null); setRangeEnd(null) }}
          style={Object.assign({}, st.toolbarBtn, rangeMode ? st.toolbarBtnActive : {})}
        >
          {rangeMode ? 'CANCEL RANGE' : 'BLOCK A RANGE'}
        </button>
        {rangeMode && rangeStart && rangeEnd && (
          <span style={st.rangeStatus}>
            {fmtNice(rangeStart)} — {fmtNice(rangeEnd)}
          </span>
        )}
      </div>

      {rangeMode && rangeStart && rangeEnd && (
        <div style={st.rangeBox}>
          <div style={st.label}>OPTIONAL REASON</div>
          <input
            value={rangeReason}
            onChange={function(e) { setRangeReason(e.target.value) }}
            placeholder="e.g. Family vacation"
            style={st.input}
          />
          <div style={st.muted}>Sundays in this range will be skipped automatically.</div>
          <button type="button" disabled={submitting} onClick={commitRange} style={st.primaryBtn}>
            {submitting ? 'BLOCKING...' : 'BLOCK THIS RANGE'}
          </button>
        </div>
      )}

      <div style={st.calendarWrap}>
        {monthPanes.map(function(p) {
          return (
            <MonthPane
              key={p.year + '-' + p.month}
              year={p.year}
              month={p.month}
              today={today}
              horizonIso={horizonIso}
              blockByIso={blockByIso}
              rangeMode={rangeMode}
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
              isInRange={inRange}
              onCellClick={clickCell}
            />
          )
        })}
        {/* Past the 3-month horizon — informational note. */}
        <div style={st.horizonNote}>
          More dates available closer to that time.
        </div>
      </div>

      {/* Single-tap modal */}
      {tapped && (
        <div style={st.modalOverlay} onClick={function() { if (!submitting) setTapped(null) }}>
          <div style={st.modal} onClick={function(e) { e.stopPropagation() }}>
            <div style={st.modalTitle}>Block off {fmtNice(tapped.iso)}?</div>
            <div style={st.label}>OPTIONAL REASON</div>
            <input
              autoFocus
              value={tapped.reason}
              onChange={function(e) { setTapped(Object.assign({}, tapped, { reason: e.target.value })) }}
              placeholder="Doctor appointment, wedding, vacation..."
              style={st.input}
            />
            <div style={st.modalActions}>
              <button type="button" disabled={submitting} onClick={function() { setTapped(null) }} style={st.ghostBtn}>
                CANCEL
              </button>
              <button type="button" disabled={submitting} onClick={commitTap} style={st.primaryBtn}>
                {submitting ? 'BLOCKING...' : 'BLOCK THIS DAY'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* List of existing blocks */}
      <div style={st.listSection}>
        <div style={st.listTitle}>MY BLOCKED DATES</div>
        {loading && blocks.length === 0 && <div style={st.empty}>Loading...</div>}
        {!loading && blocks.length === 0 && <div style={st.empty}>No blocked dates yet.</div>}
        {pending.map(function(b) { return (
          <BlockRow key={b.id} block={b} onCancel={function() { deletePending(b) }} />
        )})}
        {approved.map(function(b) { return (
          <BlockRow key={b.id} block={b} onCancel={null} />
        )})}
        {denied.map(function(b) { return (
          <BlockRow key={b.id} block={b} onCancel={null} onFindReplacement={function() { setFindReplacement(b) }} />
        )})}
      </div>

      {findReplacement && (
        <FindReplacementModal
          request={{
            id: findReplacement.id,
            user_id: findReplacement.user_id,
            request_date: findReplacement.blocked_date,
            reason: findReplacement.reason,
          }}
          onClose={function() { setFindReplacement(null) }}
        />
      )}
    </div>
  )
}

function MonthPane({ year, month, today, horizonIso, blockByIso, rangeMode, rangeStart, rangeEnd, isInRange, onCellClick }) {
  var grid = useMemo(function() { return getMonthGrid(year, month) }, [year, month])
  return (
    <div style={st.monthPane}>
      <div style={st.monthHeader}>{MONTH_LABELS[month]} {year}</div>
      <div style={st.dowRow}>
        {DOW_LABELS.map(function(d, i) {
          return <div key={i} style={Object.assign({}, st.dowCell, i === 0 ? { color: 'rgba(224,92,92,0.55)' } : {})}>{d}</div>
        })}
      </div>
      <div style={st.grid}>
        {grid.map(function(cell, idx) {
          var isOtherMonth = !cell.inMonth
          var isPast = cell.iso < today
          var isToday = cell.iso === today
          var isSunday = cell.date.getDay() === 0
          var beyond = cell.iso > horizonIso
          var block = blockByIso[cell.iso]
          var inRange = rangeMode && rangeStart && rangeEnd && isInRange(cell.iso) && !isSunday && !isPast && !beyond

          var cellStyle = Object.assign({}, st.cell)
          if (isOtherMonth) cellStyle = Object.assign(cellStyle, st.cellOtherMonth)
          if (isPast) cellStyle = Object.assign(cellStyle, st.cellPast)
          if (isSunday && !isPast) cellStyle = Object.assign(cellStyle, st.cellSunday)
          if (beyond) cellStyle = Object.assign(cellStyle, st.cellBeyond)
          if (isToday) cellStyle = Object.assign(cellStyle, st.cellToday)
          if (block && block.status === 'pending') cellStyle = Object.assign(cellStyle, st.cellPending)
          if (block && block.status === 'approved') cellStyle = Object.assign(cellStyle, st.cellApproved)
          if (block && block.status === 'denied') cellStyle = Object.assign(cellStyle, st.cellDenied)
          if (inRange && !block) cellStyle = Object.assign(cellStyle, st.cellInRange)
          if (rangeMode && (rangeStart === cell.iso || rangeEnd === cell.iso)) cellStyle = Object.assign(cellStyle, st.cellRangeAnchor)

          var clickable = !isPast && !isSunday && !beyond
          return (
            <button
              key={cell.iso + '_' + idx}
              type="button"
              disabled={!clickable}
              onClick={function() { onCellClick(cell, isPast, isSunday, beyond) }}
              style={cellStyle}
              aria-label={cell.iso}
            >
              <span style={st.cellNum}>{cell.date.getDate()}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

function BlockRow({ block, onCancel, onFindReplacement }) {
  var status = block.status || 'pending'
  var color = status === 'approved' ? '#e05c5c' : status === 'denied' ? 'rgba(255,255,255,0.4)' : '#b48c3c'
  var icon = status === 'approved' ? String.fromCodePoint(0x2713) : status === 'denied' ? String.fromCodePoint(0x2717) : String.fromCodePoint(0x23F3)
  return (
    <div style={Object.assign({}, st.blockRow, { borderLeft: '3px solid ' + color })}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={Object.assign({}, st.blockDate, status === 'denied' ? { textDecoration: 'line-through', color: 'rgba(255,255,255,0.4)' } : {})}>
          <span style={{ marginRight: '8px', color: color }}>{icon}</span>
          {fmtNice(block.blocked_date)}
        </div>
        {block.reason && <div style={st.blockReason}>{block.reason}</div>}
        {status === 'denied' && (
          <div style={st.deniedNote}>Denied — please find a replacement.</div>
        )}
      </div>
      <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
        <span style={Object.assign({}, st.statusPill, { color: color, borderColor: color + '60' })}>
          {status.toUpperCase()}
        </span>
        {onCancel && (
          <button type="button" onClick={onCancel} style={st.cancelBtn}>CANCEL</button>
        )}
        {onFindReplacement && (
          <button type="button" onClick={onFindReplacement} style={st.findBtn}>FIND REPLACEMENT</button>
        )}
      </div>
    </div>
  )
}

function Legend({ dot, label, filled }) {
  return (
    <span style={st.legendItem}>
      <span style={Object.assign({}, st.legendDot, { backgroundColor: filled === false ? 'transparent' : dot, border: '1px solid ' + dot })} />
      <span>{label}</span>
    </span>
  )
}

var st = {
  legendRow: { display: 'flex', gap: '14px', flexWrap: 'wrap', marginBottom: '10px', fontSize: '11px', color: 'rgba(255,255,255,0.5)' },
  legendItem: { display: 'inline-flex', alignItems: 'center', gap: '6px', fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 600, letterSpacing: '1.5px' },
  legendDot: { display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%' },

  toolbarRow: { display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '10px' },
  toolbarBtn: { padding: '8px 14px', backgroundColor: 'rgba(180,140,60,0.10)', border: '1px solid rgba(180,140,60,0.35)', borderRadius: '4px', color: '#b48c3c', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', fontWeight: 700, letterSpacing: '1.5px', cursor: 'pointer' },
  toolbarBtnActive: { backgroundColor: '#b48c3c', color: '#0a0f1a' },
  rangeStatus: { fontSize: '12px', color: 'rgba(255,255,255,0.6)', fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '1px' },

  rangeBox: { backgroundColor: 'rgba(180,140,60,0.06)', border: '1px solid rgba(180,140,60,0.25)', borderRadius: '4px', padding: '12px', marginBottom: '14px' },

  calendarWrap: { display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '20px' },
  monthPane: { backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '6px', padding: '10px' },
  monthHeader: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '13px', fontWeight: 700, letterSpacing: '2px', color: '#ffffff', marginBottom: '8px', textAlign: 'center' },

  dowRow: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: '4px' },
  dowCell: { textAlign: 'center', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', fontWeight: 700, letterSpacing: '1.5px', color: 'rgba(255,255,255,0.4)', padding: '4px 0' },

  grid: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '3px' },
  cell: { aspectRatio: '1 / 1', minHeight: '44px', backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '4px', color: '#ffffff', fontFamily: "'Barlow', sans-serif", fontSize: '14px', fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 },
  cellNum: { lineHeight: 1 },
  cellOtherMonth: { opacity: 0.25 },
  cellPast: { color: 'rgba(255,255,255,0.18)', backgroundColor: 'transparent', borderColor: 'rgba(255,255,255,0.03)', cursor: 'default' },
  cellSunday: { backgroundColor: 'rgba(224,92,92,0.06)', borderColor: 'rgba(224,92,92,0.18)', color: 'rgba(224,92,92,0.55)', cursor: 'default' },
  cellBeyond: { color: 'rgba(255,255,255,0.18)', backgroundColor: 'transparent', borderColor: 'rgba(255,255,255,0.03)', cursor: 'default' },
  cellToday: { boxShadow: 'inset 0 0 0 2px #b48c3c', color: '#b48c3c', fontWeight: 700 },
  cellPending: { backgroundColor: 'rgba(180,140,60,0.20)', border: '1px solid #b48c3c', color: '#0a0f1a', fontWeight: 700, cursor: 'pointer' },
  cellApproved: { backgroundColor: 'rgba(224,92,92,0.20)', border: '1px solid #e05c5c', color: '#0a0f1a', fontWeight: 700 },
  cellDenied: { textDecoration: 'line-through', color: 'rgba(255,255,255,0.35)' },
  cellInRange: { backgroundColor: 'rgba(180,140,60,0.12)', border: '1px dashed #b48c3c' },
  cellRangeAnchor: { backgroundColor: '#b48c3c', color: '#0a0f1a', border: '1px solid #b48c3c', fontWeight: 700 },

  horizonNote: { padding: '14px', textAlign: 'center', fontSize: '11px', color: 'rgba(255,255,255,0.35)', fontStyle: 'italic', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px dashed rgba(255,255,255,0.1)', borderRadius: '4px' },

  modalOverlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.65)', zIndex: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' },
  modal: { width: '100%', maxWidth: '380px', backgroundColor: '#0d1420', border: '1px solid rgba(180,140,60,0.4)', borderRadius: '6px', padding: '20px', fontFamily: "'Barlow', sans-serif" },
  modalTitle: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '15px', fontWeight: 700, letterSpacing: '1.5px', color: '#ffffff', marginBottom: '12px' },
  modalActions: { display: 'flex', gap: '8px', marginTop: '14px' },

  label: { display: 'block', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', fontWeight: 700, letterSpacing: '2px', color: 'rgba(255,255,255,0.4)', marginBottom: '6px' },
  input: { width: '100%', padding: '10px 12px', backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: '#ffffff', fontSize: '14px', fontFamily: "'Barlow', sans-serif", outline: 'none', boxSizing: 'border-box', marginBottom: '8px' },
  muted: { fontSize: '11px', color: 'rgba(255,255,255,0.4)', margin: '4px 0 10px' },

  primaryBtn: { flex: 1, padding: '11px', backgroundColor: '#b48c3c', border: 'none', borderRadius: '4px', color: '#0a0f1a', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '2px', cursor: 'pointer' },
  ghostBtn: { padding: '11px 16px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '4px', color: 'rgba(255,255,255,0.55)', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '1.5px', cursor: 'pointer' },

  listSection: { marginTop: '20px' },
  listTitle: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', letterSpacing: '2px', color: 'rgba(255,255,255,0.4)', marginBottom: '8px' },
  empty: { padding: '20px', textAlign: 'center', color: 'rgba(255,255,255,0.25)', fontSize: '13px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px dashed rgba(255,255,255,0.06)', borderRadius: '4px' },

  blockRow: { display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '10px 12px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '4px', marginBottom: '6px' },
  blockDate: { fontSize: '14px', fontWeight: 600, color: '#ffffff' },
  blockReason: { fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginTop: '2px' },
  deniedNote: { fontSize: '11px', color: '#e05c5c', marginTop: '4px', fontStyle: 'italic' },

  statusPill: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', fontWeight: 700, letterSpacing: '1.5px', padding: '3px 8px', borderRadius: '3px', border: '1px solid', alignSelf: 'flex-start' },
  cancelBtn: { padding: '6px 10px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '3px', color: 'rgba(255,255,255,0.5)', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', fontWeight: 700, letterSpacing: '1px', cursor: 'pointer' },
  findBtn: { padding: '6px 10px', backgroundColor: 'rgba(180,140,60,0.10)', border: '1px solid rgba(180,140,60,0.35)', borderRadius: '3px', color: '#b48c3c', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', fontWeight: 700, letterSpacing: '1px', cursor: 'pointer' },
}
